import { BetSelection, EventPeriod, EventType, MarketType, Teamwhere } from '@/constants';
import { AppError } from '@/errors/appError';
import { AbstractModel } from '@/models/abstractModel';
import { ITurnoverApi } from '@/types';

export class TurnoverSelectionItem extends AbstractModel {
    public balance: number;
    public betCount: number;
    public correctScore: string | null;
    public customEventId: string;
    public customLeagueLimitsCategoryId: number | null;
    public customLeagueName: string;
    public customTeamAwayName: string;
    public customTeamHomeName: string;
    public eventDate: string;
    public eventType: EventType;
    public handicap: string | null;
    public marketTypeId: MarketType;
    public netPayoutSum: number;
    public oddsAvg: number;
    public periodTypeId: EventPeriod;
    public selectionId: number;
    public settledBetCount: number;
    public stakeSum: number;
    public teamwhere: Teamwhere;
    public unsettledBetCount: number;
    public userCount: number;

    constructor(data: ITurnoverApi) {
        super();
        this.customEventId = data.custom_event_id;
        this.eventDate = data.date;
        this.periodTypeId = data.period_type_id;
        this.betCount = data.bet_count;
        this.settledBetCount = data.settled_bet_count;
        this.unsettledBetCount = data.unsettled_bet_count;
        this.stakeSum = data.stake_sum;
        this.netPayoutSum = data.net_payout_sum;
        this.balance = data.balance;
        this.handicap = data.handicap;
        this.customLeagueName = data.custom_league_name;
        this.customLeagueLimitsCategoryId = data.custom_league_limits_category_id;
        this.customTeamHomeName = data.custom_team_home_name;
        this.customTeamAwayName = data.custom_team_away_name;
        this.eventType = data.is_live === 1 ? EventType.Live : EventType.Pregame;
        this.marketTypeId = data.market_type_id as MarketType;
        this.userCount = data.user_count;
        this.teamwhere = data.teamwhere;
        this.correctScore = data.correct_score;
        this.oddsAvg = data.odds_avg;
        this.selectionId = data.selection_id;
    }

    get displayedHandicap() {
        if (
            this.marketTypeId === MarketType.Handicap ||
            this.marketTypeId === MarketType.CornersHandicap ||
            this.marketTypeId === MarketType.Spread ||
            this.marketTypeId === MarketType.Goal ||
            this.marketTypeId === MarketType.CornersGoal ||
            this.marketTypeId === MarketType.GoalHome ||
            this.marketTypeId === MarketType.GoalAway
        ) {
            if (Number(this.handicap) == 0) {
                return '0';
            }

            const value = parseFloat(this.handicap as string);
            const isHandicap =
                this.selectionId === BetSelection.HandicapHome ||
                this.selectionId === BetSelection.HandicapAway ||
                this.selectionId === BetSelection.CornersHandicapHome ||
                this.selectionId === BetSelection.CornersHandicapAway ||
                this.selectionId === BetSelection.SpreadHome ||
                this.selectionId === BetSelection.SpreadAway;
            const prefix = isHandicap && value > 0 ? '+' : '';
            return prefix + value.toFixed(2);
        }

        return this.handicap;
    }

    get uniqKey(): string {
        return [
            this.customEventId,
            this.eventDate,
            this.eventType,
            this.selectionId,
            this.teamwhere,
            this.handicap,
            this.correctScore,
            this.marketTypeId,
            this.periodTypeId,
        ].join('|');
    }

    public getEventTypeName(isAbbr: boolean = false): string {
        if (this.eventType === EventType.Live) {
            return isAbbr ? this.t('common.marketLiveShort') : this.t('common.marketLive');
        } else {
            return isAbbr ? this.t('common.marketDballShort') : this.t('common.marketDball');
        }
    }

    public getMarketTypeName(isAbbr: boolean = false): string {
        return this._lazyLoad('getMarketTypeName', isAbbr, () => {
            switch (this.marketTypeId) {
                case MarketType.Handicap:
                    return isAbbr ? this.t('common.handicapAbbr') : this.t('common.handicap');
                case MarketType.Goal:
                    return isAbbr ? this.t('common.goalAbbr') : this.t('common.over') + '/' + this.t('common.under');
                case MarketType.OneXTwo:
                    return this.t('common.1X2');
                case MarketType.Bts:
                    return isAbbr ? this.t('common.bts') : this.t('common.btts');
                case MarketType.Dc:
                    return isAbbr ? this.t('common.dc') : this.t('common.doubleChance');
                case MarketType.Cs:
                    return isAbbr ? this.t('common.csAbbr') : this.t('common.correctScore');
                case MarketType.CornersHandicap:
                    return isAbbr ? this.t('common.cornersHandicapAbbr') : this.t('common.cornersAsianHandicap');
                case MarketType.CornersGoal:
                    return isAbbr ? this.t('common.cornersGoalAbbr') : this.t('common.cornersOver') + '/' + this.t('common.cornersUnder');
                case MarketType.GoalHome:
                    return isAbbr ? this.t('dictionaries.marketType.short.goalHome') : this.t('dictionaries.marketType.full.goalHome');
                case MarketType.GoalAway:
                    return isAbbr ? this.t('dictionaries.marketType.short.goalAway') : this.t('dictionaries.marketType.full.goalAway');
                case MarketType.DrawNoBet:
                    return isAbbr ? this.t('dictionaries.marketType.short.drawNoBet') : this.t('dictionaries.marketType.full.drawNoBet');
                case MarketType.Moneyline:
                    return isAbbr ? this.t('dictionaries.marketType.short.moneyline') : this.t('dictionaries.marketType.full.moneyline');
                case MarketType.Spread:
                    return isAbbr ? this.t('dictionaries.marketType.short.spread') : this.t('dictionaries.marketType.full.spread');
                default:
                    throw new AppError(`Unable to defined marketType #${this.marketTypeId}`);
            }
        });
    }

    public getPeriodName(isAbbr: boolean = false): string {
        if (this.isFT()) {
            return isAbbr ? this.t('common.periodFTAbbr') : this.t('common.periodFT');
        }

        return isAbbr ? this.t('common.period1HAbbr') : this.t('common.period1H');
    }

    public getSelectionName(withValue: boolean = false): string {
        return this._lazyLoad('selectionName', withValue, () => {
            //specific for CS
            if (this.selectionId === BetSelection.Cs) {
                return this.t('common.correctScore') + ' ' + this.correctScore;
            }

            let result = '';
            if (this.selectionId === BetSelection.HandicapHome || this.selectionId === BetSelection.HandicapAway) {
                result = this.teamwhere === Teamwhere.Home ? this.t('common.home') : this.t('common.away');
                result += ' ';
            } else if (this.selectionId === BetSelection.SpreadHome || this.selectionId === BetSelection.SpreadAway) {
                result = this.teamwhere === Teamwhere.Home ? this.t('common.home') : this.t('common.away');
                result += ' ';
            } else if (this.selectionId === BetSelection.CornersHandicapHome || this.selectionId === BetSelection.CornersHandicapAway) {
                result = this.teamwhere === Teamwhere.Home ? this.t('common.cornersHome') : this.t('common.cornersAway');
                result += ' ';
            } else if (this.selectionId === BetSelection.GoalOver) {
                result = this.t('common.over');
            } else if (this.selectionId === BetSelection.GoalUnder) {
                result = this.t('common.under');
            } else if (this.selectionId === BetSelection.GoalHomeOver) {
                result = this.t('common.home');
                result += ' ';
                result += this.t('common.over');
            } else if (this.selectionId === BetSelection.GoalHomeUnder) {
                result = this.t('common.home');
                result += ' ';
                result += this.t('common.under');
            } else if (this.selectionId === BetSelection.GoalAwayOver) {
                result = this.t('common.away');
                result += ' ';
                result += this.t('common.over');
            } else if (this.selectionId === BetSelection.GoalAwayUnder) {
                result = this.t('common.away');
                result += ' ';
                result += this.t('common.under');
            } else if (this.selectionId === BetSelection.CornersGoalOver) {
                result = this.t('common.cornersOver');
            } else if (this.selectionId === BetSelection.CornersGoalUnder) {
                result = this.t('common.cornersUnder');
            } else if (this.selectionId === BetSelection.OneXTwoHome) {
                result = this.t('common.home');
            } else if (this.selectionId === BetSelection.OneXTwoDraw) {
                result = this.t('common.draw');
            } else if (this.selectionId === BetSelection.OneXTwoAway) {
                result = this.t('common.away');
            } else if (this.selectionId === BetSelection.DrawNoBetHome) {
                result = this.t('dictionaries.betSelection.drawNoBetHome');
            } else if (this.selectionId === BetSelection.DrawNoBetAway) {
                result = this.t('dictionaries.betSelection.drawNoBetAway');
            } else if (this.selectionId === BetSelection.MoneylineHome) {
                result = this.t('dictionaries.betSelection.moneylineHome');
            } else if (this.selectionId === BetSelection.MoneylineAway) {
                result = this.t('dictionaries.betSelection.moneylineAway');
            } else if (this.selectionId === BetSelection.BtsYes) {
                result = this.t('common.bts') + ' - ' + this.t('common.yes');
            } else if (this.selectionId === BetSelection.BtsNo) {
                result = this.t('common.bts') + ' - ' + this.t('common.no');
            } else if (this.selectionId === BetSelection.Dc1X) {
                result = this.t('common.home') + this.t('common.or') + this.t('common.draw');
            } else if (this.selectionId === BetSelection.Dc12) {
                result = this.t('common.home') + this.t('common.or') + this.t('common.away');
            } else if (this.selectionId === BetSelection.DcX2) {
                result = this.t('common.draw') + this.t('common.or') + this.t('common.away');
            } else {
                throw new AppError(`Unknown betSelection ${this.selectionId}`);
            }

            return result;
        });
    }

    public isFT(): boolean {
        return this.periodTypeId === EventPeriod.FullTime;
    }
}
